// TODO(backend/activity): The current "Activity log" card below shows a mock *activity feed*
// (status changes, crew joins, etc.). The backend `LogbookEntries` query returns *nautical log
// entries* (wind speed, sea state, barometric pressure, lat/lng, notes). These are fundamentally
// different shapes and need a UX decision before the activity feed can be wired:
//   (a) add a dedicated nautical log tab
//   (b) replace the activity feed design
//   (c) build a separate admin-style activity feed endpoint
// For now: the activity feed card keeps mock data; the nautical log card is wired to real data.

import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bowfast/ui/components/card";
import { Input } from "@bowfast/ui/components/input";
import { Label } from "@bowfast/ui/components/label";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@bowfast/ui/components/sheet";
import { motion } from "framer-motion";
import { BookOpenIcon, ClockIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import * as z from "zod";
import type { AddLogbookEntryInput, SeaState } from "~/graphql/generated";
import { fadeUp, stagger } from "~/lib/animations";
import { SEA_STATE_LABEL } from "~/lib/expedition-constants";
import { fmtEntryDayUtc, fmtEntryTimeUtc, groupEntriesByDay, logAuthorInitials } from "~/lib/expedition-utils";
import type { ActivityEntry } from "~/pages/expeditions/expedition-detail.mock";
import type { LogbookEntry } from "~/pages/expeditions/expeditions.types";
import { ActivityItem } from "./ActivityItem";

const addEntrySchema = z.object({
  notes: z.string().optional(),
  windSpeedKnotsStr: z.string().optional(),
});

const SEA_STATES: SeaState[] = ["CALM", "SLIGHT", "MODERATE", "ROUGH", "VERY_ROUGH", "HIGH"];

export function LogTab({
  activity,
  logbookEntries,
  onAddEntry,
  onDeleteEntry,
  isMutating,
}: Readonly<{
  activity: ActivityEntry[];
  logbookEntries?: LogbookEntry[];
  onAddEntry?: (input: AddLogbookEntryInput) => void;
  onDeleteEntry?: (id: string) => void;
  isMutating?: boolean;
}>) {
  // ── Add entry form state ───────────────────────────────────────────────────
  const [notes, setNotes] = useState("");
  const [windStr, setWindStr] = useState("");
  const [seaState, setSeaState] = useState<SeaState | "">("");
  const [formErr, setFormErr] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  function handleAddEntry() {
    const result = addEntrySchema.safeParse({ notes, windSpeedKnotsStr: windStr });
    if (!result.success) {
      setFormErr(result.error.issues[0]?.message ?? "Invalid input");
      return;
    }
    setFormErr(null);
    const input: AddLogbookEntryInput = {
      entryTime: new Date().toISOString(),
      ...(notes.trim() && { notes: notes.trim() }),
      ...(windStr && !Number.isNaN(Number(windStr)) && { windSpeedKnots: Number(windStr) }),
      ...(seaState && { seaState }),
    };
    onAddEntry?.(input);
    setNotes("");
    setWindStr("");
    setSeaState("");
    setSheetOpen(false);
  }

  const hasEntries = !!logbookEntries?.length;
  const dayGroups = hasEntries ? groupEntriesByDay(logbookEntries ?? []) : [];

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-5">
      {/* ── Nautical log (real data) ── */}
      {(hasEntries || !!onAddEntry) && (
        <motion.div variants={fadeUp}>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <BookOpenIcon className="size-4 text-abyss-500" />
                  Nautical log
                </CardTitle>
                {onAddEntry && (
                  <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                    <SheetTrigger render={<Button size="sm" variant="outline" className="gap-1.5" />}>
                      <PlusIcon className="size-3.5" />
                      Add entry
                    </SheetTrigger>
                    <SheetContent side="right">
                      <SheetHeader>
                        <SheetTitle>Add log entry</SheetTitle>
                      </SheetHeader>
                      <div className="flex flex-col gap-4 px-5 py-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label htmlFor="entry-wind">Wind (knots)</Label>
                            <Input
                              id="entry-wind"
                              placeholder="e.g. 12"
                              type="number"
                              min="0"
                              max="120"
                              value={windStr}
                              onChange={(e) => setWindStr(e.target.value)}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor="entry-sea">Sea state</Label>
                            <select
                              id="entry-sea"
                              className="field-input field-select"
                              value={seaState}
                              onChange={(e) => setSeaState(e.target.value as SeaState | "")}
                            >
                              <option value="">—</option>
                              {SEA_STATES.map((s) => (
                                <option key={s} value={s}>
                                  {SEA_STATE_LABEL[s]}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="entry-notes">Notes</Label>
                          <Input
                            id="entry-notes"
                            placeholder="Watch observations, port calls, sail changes…"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                          />
                        </div>
                        {/* TODO(backend/log): expose lat/lng, heading, barometricPressure,
                            engineOn, speedOverGround to this form when GPS integration is ready */}
                        {formErr && <p className="text-xs text-destructive">{formErr}</p>}
                      </div>
                      <SheetFooter className="px-5 pb-5">
                        <SheetClose render={<Button variant="outline" />}>Cancel</SheetClose>
                        <Button onClick={handleAddEntry} disabled={isMutating}>
                          Add entry
                        </Button>
                      </SheetFooter>
                    </SheetContent>
                  </Sheet>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {!hasEntries ? (
                <p className="px-5 py-8 text-center text-sm text-muted-foreground">
                  No log entries yet — add the first one above.
                </p>
              ) : (
                dayGroups.map(([day, entries]) => (
                  <div key={day}>
                    <p className="border-b border-border bg-muted/40 px-5 py-2 text-[0.6875rem] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                      {fmtEntryDayUtc(entries[0]?.entryTime ?? day)}
                    </p>
                    {entries.map((entry) => (
                      <div
                        key={entry.id}
                        className="flex items-start gap-4 border-b border-border px-5 py-3.5 last:border-b-0 transition-colors hover:bg-muted/20"
                      >
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-abyss-100 text-[0.6875rem] font-bold text-abyss-700 dark:bg-abyss-900 dark:text-abyss-300">
                          {logAuthorInitials(entry.author)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            <span className="font-heading text-sm font-semibold tabular-nums text-foreground">
                              {fmtEntryTimeUtc(entry.entryTime)}
                            </span>
                            {entry.windSpeedKnots != null && <span>{entry.windSpeedKnots} kt</span>}
                            {entry.seaState && <span>{SEA_STATE_LABEL[entry.seaState]}</span>}
                            {entry.speedOverGround != null && <span>SOG {entry.speedOverGround} kt</span>}
                            {entry.courseOverGround != null && <span>COG {entry.courseOverGround}°</span>}
                          </div>
                          {entry.notes && <p className="mt-0.5 text-sm">{entry.notes}</p>}
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {entry.author.displayName || `${entry.author.firstName} ${entry.author.lastName}`}
                          </p>
                        </div>
                        {onDeleteEntry && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="shrink-0 text-muted-foreground hover:text-destructive"
                            disabled={isMutating}
                            onClick={() => onDeleteEntry(entry.id)}
                          >
                            <Trash2Icon className="size-3.5" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* ── Activity log (mock — see TODO at top of file) ── */}
      <motion.div variants={fadeUp}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <ClockIcon className="size-4 text-abyss-500" />
              Activity log
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {activity.map((a, i) => (
              <ActivityItem key={a.id} entry={a} isLast={i === activity.length - 1} />
            ))}
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
